import React from "react";
import { createPortal } from "react-dom";

type BottomDrawerProps = {
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
  /** 열릴 때 포커스 이동할지 (기본 true) */
  autoFocus?: boolean;
  /** 최대 높이 비율 (0~1), 기본 0.85 = 화면의 85% */
  maxHeightRatio?: number;
  /** 드래그로 닫기 사용 여부 (기본 true) */
  swipeToClose?: boolean;
  /** 바닥 여백(safe-area 포함) 보정 */
  bottomPadding?: string; // e.g. "calc(16px + env(safe-area-inset-bottom))"
  className?: string;
};

export default function BottomDrawer({
  open,
  onClose,
  children,
  autoFocus = true,
  maxHeightRatio = 0.85,
  swipeToClose = true,
  bottomPadding = "calc(16px + var(--safe-area-inset-bottom))",
  className = "",
}: BottomDrawerProps) {
  const sheetRef = React.useRef<HTMLDivElement | null>(null);
  const scrollRef = React.useRef<HTMLDivElement | null>(null);
  const startY = React.useRef<number | null>(null);
  const currentY = React.useRef<number>(0);
  const dragging = React.useRef(false);
  const [dragOffset, setDragOffset] = React.useState(0); // px

  // Body scroll lock
  React.useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  // ESC 닫기
  React.useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // 오픈 시 스크롤 초기화 & 포커스
  React.useEffect(() => {
    if (open) {
      scrollRef.current?.scrollTo({ top: 0 });
      if (autoFocus) {
        const t = setTimeout(() => sheetRef.current?.focus(), 50);
        return () => clearTimeout(t);
      }
    }
  }, [open, autoFocus]);

  // 드래그 핸들러
  const onPointerDown = (e: React.PointerEvent) => {
    if (!swipeToClose) return;
    dragging.current = true;
    startY.current = e.clientY;
    currentY.current = e.clientY;
    (e.target as Element).setPointerCapture?.(e.pointerId);
  };

  const onPointerMove = (e: React.PointerEvent) => {
    if (!dragging.current || startY.current == null) return;
    currentY.current = e.clientY;
    const dy = Math.max(0, currentY.current - startY.current);
    setDragOffset(dy);
  };

  const onPointerUp = () => {
    if (!dragging.current) return;
    dragging.current = false;
    const threshold = 120; // 이 이상 내리면 닫기
    if (dragOffset > threshold) {
      setDragOffset(0);
      onClose();
    } else {
      // 원위치
      setDragOffset(0);
    }
  };

  if (typeof document === "undefined") return null;

  return createPortal(
    <div
      aria-hidden={!open}
      className={[
        "fixed inset-0 z-[1000]",
        open ? "pointer-events-auto" : "pointer-events-none",
      ].join(" ")}
    >
      {/* Backdrop */}
      <div
        onClick={onClose}
        className={[
          "absolute inset-0 bg-black/40 transition-opacity",
          open ? "opacity-100" : "opacity-0",
        ].join(" ")}
      />

      {/* Sheet */}
      <div
        role="dialog"
        aria-modal="true"
        ref={sheetRef}
        tabIndex={-1}
        className={[
          "absolute left-0 right-0",
          "mx-auto max-w-[720px]",
          "rounded-t-2xl bg-white shadow-lg",
          "transition-transform duration-300 will-change-transform",
          "focus:outline-none",
          className,
        ].join(" ")}
        style={{
          // 위치/크기
          bottom: 0,
          transform: open
            ? `translateY(${dragOffset}px)`
            : "translateY(100%)",
          maxHeight: `min(${Math.round(maxHeightRatio * 100)}vh, 100dvh)`,
          paddingBottom: bottomPadding,
        }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      >
        {/* 핸들 바 (드래그 영역 시각화) */}
        <div className="sticky top-0 z-10 bg-white pt-3 pb-2 rounded-t-2xl">
          <div className="mx-auto h-1.5 w-10 rounded-full bg-black/15" />
        </div>

        {/* 내용 영역 */}
        <div ref={scrollRef} className="px-[15px] overflow-auto"
          style={{ maxHeight: `calc(${Math.round(maxHeightRatio * 100)}vh - 56px)` }}>
          {children}
        </div>
      </div>
    </div>,
    document.body
  );
}
